import { store } from '../store';
import { setIsSalespersonHost } from '../store/conversationSlice';
import { recallWebSocketService } from './recallWebSocketService';
import { sessionManager } from './sessionManager';
import { connectAlertSocketsToRoom } from './liveCallSockets';
import { clearLiveSessionData, restoreSessionHistory } from './liveSessionData';
import { updateBotClient } from './botApi';
import { generateClientId } from '../utils/clientId';
import { getBotAuthErrorMessage } from '../utils/botApiErrors';
import { BOT_API_COPY } from '../constants/botApi';
import { parseServerTimestampMs } from '../utils/serverTimestamp';

export interface BotJoinRequest {
  /** The Recall bot id: also the room and the conversationsessions id. */
  botId: string;
  /** The recall relay's client socket; without it no transcript arrives. */
  websocketUrl?: string;
  isSalespersonHost: boolean;
  /**
   * The signed-in user added this bot. A watcher (false) joins read-only: it
   * changes nothing on the bot (the client-id update is the owner's; the
   * backend refuses anyone else's) and the page hides every owner control.
   */
  isOwner: boolean;
}

export interface BotJoinResult {
  /** For the rep when the owner's client-id update was refused (401/403); null otherwise. */
  clientUpdateWarning: string | null;
  /** The call's name from its saved session; null before the saver has written it. */
  sessionName: string | null;
}

/** Point the bot's session at this browser. Not critical to joining, so only an auth refusal is reported. */
const pointBotAtThisClient = async (botId: string): Promise<string | null> => {
  try {
    await updateBotClient(botId, generateClientId());
    return null;
  } catch (error) {
    console.warn('[botSessionJoin] Could not update the bot client id:', error);
    return getBotAuthErrorMessage(error, BOT_API_COPY.joinForbidden);
  }
};

/**
 * Join a live bot call, as its owner or as a watcher: leave the previous
 * bot's socket first (so nothing syncs the cleared store to it), clear the
 * store, open the recall relay for this bot, point production, landmine and
 * competitor at its room, then restore the call so far as silent history
 * (GET /api/sessions/:id/state). The live sockets open before the history
 * loads, so nothing said in between is lost; the history skips what they
 * already delivered.
 *
 * A failed recall connect is logged and the rest still runs (the other
 * sockets may work, and the recall socket retries on its own). A failure to
 * reach production rejects.
 */
export const joinBotSession = async ({
  botId,
  websocketUrl,
  isSalespersonHost,
  isOwner,
}: BotJoinRequest): Promise<BotJoinResult> => {
  recallWebSocketService.disconnect();
  clearLiveSessionData();

  sessionManager.setRoomId(botId);
  // The previous call's start never carries over: the saved start (below) or
  // the bot's first participant_join sets this call's.
  sessionManager.resetCallTiming();
  // Before production connects: its session_config carries it.
  store.dispatch(setIsSalespersonHost(isSalespersonHost));

  let clientUpdateWarning: string | null = null;
  if (websocketUrl) {
    try {
      await recallWebSocketService.connect(botId, websocketUrl);
      if (isOwner) clientUpdateWarning = await pointBotAtThisClient(botId);
    } catch (recallError) {
      console.error(`[botSessionJoin] Recall socket for ${botId} failed to connect:`, recallError);
    }
  } else {
    console.warn(`[botSessionJoin] No recall socket URL for ${botId}: no live transcript`);
  }

  await connectAlertSocketsToRoom(botId);
  const savedState = await restoreSessionHistory(botId);
  // The call's real start, for the live header on a watch or resume.
  const startedAtMs = parseServerTimestampMs(savedState?.startTime);
  if (startedAtMs !== null) sessionManager.setCallStartTime(new Date(startedAtMs));
  return { clientUpdateWarning, sessionName: savedState?.sessionName || null };
};
